/**
 * StudioLibraryFilters — client-side filter bar for the Studio library.
 * Filters the existing `library` list (no backend fetch) so the creator
 * can quickly find e.g. "all Balkans episodes" and categorise them as a
 * group, or surface every uncategorised story.
 *
 * Filters:
 *   - Free-text: matches title / country / tags
 *   - Main category: dropdown of the 11 slugs
 *   - Uncategorised-only toggle
 *
 * Returns `{query, applyTo(list) -> filteredList}` via a stable callback
 * so the parent can render the filtered grid inline.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, X, Filter } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let _catCache = null;

export default function StudioLibraryFilters({ library, onChange }) {
  const [q, setQ] = useState("");
  const [mainCategory, setMainCategory] = useState("");
  const [uncategorisedOnly, setUncategorisedOnly] = useState(false);
  const [publishedOnly, setPublishedOnly] = useState(false);
  const [cats, setCats] = useState(_catCache);

  useEffect(() => {
    if (_catCache) return;
    axios.get(`${API}/taxonomy`).then(({ data }) => {
      _catCache = data.main_categories;
      setCats(_catCache);
    });
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (library || []).filter((v) => {
      if (uncategorisedOnly && v.main_category) return false;
      if (mainCategory && v.main_category !== mainCategory) return false;
      if (publishedOnly && !(v.status === "done" && v.published)) return false;
      if (needle) {
        const hay = [
          v.title,
          v.country,
          v.location,
          v.organization_name,
          v.slug,
          ...(v.tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [library, q, mainCategory, uncategorisedOnly, publishedOnly]);

  // Push the filtered list up whenever it changes. The parent renders
  // the grid off this list; when no filters are active, the list is
  // identical to `library`.
  useEffect(() => {
    onChange?.(filtered);
  }, [filtered, onChange]);

  const activeCount =
    (q ? 1 : 0) +
    (mainCategory ? 1 : 0) +
    (uncategorisedOnly ? 1 : 0) +
    (publishedOnly ? 1 : 0);

  const clearAll = () => {
    setQ("");
    setMainCategory("");
    setUncategorisedOnly(false);
    setPublishedOnly(false);
  };

  return (
    <div
      className="panel p-4 mb-6 flex flex-wrap items-center gap-3"
      data-testid="studio-library-filters"
    >
      <div className="relative flex-1 min-w-[240px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[color:var(--muted)]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find in your library — title, tag, place…"
          className="w-full bg-transparent border border-white/10 rounded-full px-9 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
          data-testid="studio-lib-search"
        />
        {q && (
          <button
            onClick={() => setQ("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--muted)] hover:text-white"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <select
        value={mainCategory}
        onChange={(e) => setMainCategory(e.target.value)}
        className="bg-transparent border border-white/10 rounded-full px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
        data-testid="studio-lib-category-select"
      >
        <option value="" className="bg-black">
          All categories
        </option>
        {(cats || []).map((c) => (
          <option key={c.slug} value={c.slug} className="bg-black">
            {c.label}
          </option>
        ))}
      </select>
      <button
        onClick={() => setUncategorisedOnly((v) => !v)}
        className={`text-xs px-3 py-2 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
          uncategorisedOnly
            ? "border-amber-400/60 text-amber-300 bg-amber-500/10"
            : "border-white/10 hover:border-white/25"
        }`}
        data-testid="studio-lib-uncat-toggle"
        title="Show only stories that don't have a main category yet"
      >
        <Filter className="w-3.5 h-3.5" />
        Uncategorised only
      </button>
      <button
        onClick={() => setPublishedOnly((v) => !v)}
        className={`text-xs px-3 py-2 rounded-full border transition-colors ${
          publishedOnly
            ? "border-emerald-400/60 text-emerald-300 bg-emerald-500/10"
            : "border-white/10 hover:border-white/25"
        }`}
        data-testid="studio-lib-published-toggle"
        title="Show only published stories"
      >
        Published only
      </button>
      <div className="ml-auto flex items-center gap-3 text-xs text-[color:var(--muted)]">
        <span data-testid="studio-lib-count">
          Showing <span className="text-white font-medium">{filtered.length}</span> of{" "}
          {(library || []).length}
        </span>
        {activeCount > 0 && (
          <button
            onClick={clearAll}
            className="underline underline-offset-2 hover:text-white"
            data-testid="studio-lib-clear-filters"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
